import { getCurrentEnvironment } from './components/FeatureFlag';
import {
  getResearchTaskScope,
  VIEW_PARTTIME_RESEARCH_HOURS_PERMISSION,
  EDUCATIONAL_RESEARCH_TASKS_ROUTE,
} from './utils/researchTaskPermissions';
import { getRestrictedOrderAccess } from './utils/restrictedOrderAccess';
export default function access(initialState) {
  const { currentUser, permissions, hasPermission } = initialState || {};
  if (!currentUser || !currentUser.id) return {};

  const canFollowOrder = hasPermission({
    route: 'sales',
    function: 'follow-up-order',
    scope: 'all',
  });

  const canQuerySalarySubsidy = hasPermission({
    route: 'salary',
    function: 'query-subsidy',
  });

  const canQuerySalaryAdjustment = hasPermission({
    route: 'salary',
    function: 'view-salary-adjustment',
  });

  const canViewMentorOperationsFollow = hasPermission({
    route: 'mentorOperationsFollow',
    function: 'query',
  });
  const canSendOperationCourseConsumptionRemoteWorkGoodNews = hasPermission({
    route: 'salary',
    function: 'send-operation-course-consumption-remote-work-good-news',
    scope: 'all',
  });
  const canExportOperationPaycheck = hasPermission({
    route: 'salary',
    function: 'export-operation-paycheck',
    scope: 'all',
  });

  const permissionScopes = currentUser.permissionScopes;
  const roles = currentUser.roles?.map?.((r) => r.name || r);
  const isSuperAdmin = currentUser.permissionGroupAuthorizationEnabled
    ? currentUser.isSystemAdmin === true
    : roles?.includes?.('superadmin');
  const isAdmin = roles?.includes?.('admin');
  const isHR = roles?.includes?.('hr');
  const isFinance = roles?.includes?.('finance');
  const isOperation = roles?.includes?.('operations');
  const isOperationAdmin = roles?.includes?.('operation_admin');
  const isSales = roles?.includes?.('sales');
  const isMatchMaker = roles?.includes?.('matchmaker');
  const isRecruiter = roles?.includes?.('recruiter');
  const isEduHR = roles?.includes?.('eduHR');
  const isQualityInspector = roles?.includes?.('qualityInspector');
  const isTeacher = roles?.includes?.('teacher');
  const isSalesAdmin = roles?.includes?.('sales_admin');
  const isOperationSupervisor = roles?.includes?.('operation_supervisor');
  const canSeeAllSalesPage = isSuperAdmin || isSales || isSalesAdmin || isHR || isFinance;
  const {
    canCreateRestrictedOrder,
    canAccessSalesLeads,
    canViewSalableProducts,
    canAccessSalesCustomers,
    canCreateSalesOrder,
    canQueryOrders,
    canContract,
  } = getRestrictedOrderAccess({
    permissions,
    permissionScopes,
    canSeeAllSalesPage,
    canFollowOrder,
    hasLegacyContractAccess: hasPermission({ route: 'contract', function: 'query' }) &&
      (hasPermission({ route: 'contract', function: 'query', scope: 'all' }) ||
        !permissionScopes?.contract?.query?.some(item => item.scope.name === 'product_groups')),
  });
  const canQuerySalesData = hasPermission({ route: 'sales', function: 'query-sales-data' });
  const canQueryKASales = hasPermission({ route: 'sales', function: 'query-ka-sales' });
  const canAccessSaleData = canQuerySalesData || canQueryKASales;
  const canSales =
    canCreateSalesOrder || canQueryOrders || canAccessSalesLeads || canViewSalableProducts ||
    canAccessSaleData;
  const canSeeMentorOperationsFollow = isSuperAdmin || isAdmin || isMatchMaker;
  const canViewMentorOperationsMatchingDashboard = isSuperAdmin || isMatchMaker || isRecruiter;
  const canViewRecruitmentPerformance = isSuperAdmin || Number(currentUser.id) === 287;
  const canViewRecruitmentGap = hasPermission({
    route: 'recruitmentGap',
    function: 'view-risk-warning',
  });
  const canConfigureRecruitmentGap = hasPermission({ route: 'recruitmentGap', function: 'configure' });
  const canManageTeacherTraining = hasPermission({
    route: 'teacher-training',
    function: 'manage-all',
  });
  const canViewTeacherTraining =
    canManageTeacherTraining ||
    hasPermission({ route: 'teacher-training', function: 'view-edit-own' });
  const canServiceData = isSuperAdmin;

  const canOrganization = !!permissions?.organization?.length;
  const canViewAllPerformanceData = !!permissions?.data_center?.includes(
    'view-all-teams-performance-raw-data',
  );

  const canAccount = !!permissions?.account?.length;
  const canUsePermissionGroups = !currentUser.isServiceAccount && !currentUser.isLocked
    && !currentUser.deletedAt && !['resigned', 'optimized'].includes(currentUser.teacherStatus);
  const canUseGroupPermission = (fn) => !!(canUsePermissionGroups && (
    isSuperAdmin || (currentUser.permissionGroupAuthorizationEnabled &&
      permissions?.permission?.includes(fn))
  ));
  const canViewPermissionGroups = canUseGroupPermission('viewGroups');
  const canEditPermissionGroups = canUseGroupPermission('editGroups');
  const canEditPermissionGroupMembers = canUseGroupPermission('editGroupMembers');
  const canUpdateRolePermissions =
    !currentUser.permissionGroupAuthorizationEnabled &&
    permissionScopes.permission?.editRole?.find((item) => item.scope.name === 'all');
  const canUpdateUserPermissions =
    !currentUser.permissionGroupAuthorizationEnabled &&
    permissionScopes.permission?.editUser?.find((item) => item.scope.name === 'all');
  const canViewTeacherReview = !!permissions?.account?.includes('viewTeacherReview');
  const canOperation = ['query', 'create', 'update', 'delete', 'restore'].some((fn) =>
    hasPermission({ route: 'operations', function: fn }));
  const canTeacher = !!permissions?.teacher?.length;

  const canFulltimeTeacher = getResearchTaskScope(hasPermission, 'management') === 'all';
  const canAccessOnboarding = !!permissions?.onboarding?.length;
  const canAccessHR = !!permissions?.hr?.length;
  const canAccessSecretTeacherFeedback = !!permissions?.teacherFeedback?.length;
  const canAssignCourse = !!permissions?.trainingCourse?.includes('assign');

  const canServiceAgreement = isSuperAdmin || isAdmin || isFinance || isSalesAdmin || isSales;

  const canQueryAllFullTimeTeacherSalary = hasPermission({
    route: 'fulltimeTeacherSalary',
    function: 'query-fulltime-teacher-salary',
    scope: 'all',
  });

  const canQueryPartialFullTimeTeacherSalary =
    hasPermission({
      route: 'fulltimeTeacherSalary',
      function: 'query-fulltime-teacher-salary',
      scope: 'partial',
    }) && currentUser.isFullTimeTeacher === true;

  const canComplaint =
    isAdmin ||
    isSuperAdmin ||
    isRecruiter ||
    isMatchMaker ||
    isQualityInspector ||
    isEduHR ||
    isOperation ||
    isOperationAdmin;
  // const canQueryCourseMatch = !!permissions?.courseMatch?.includes('query');

  const canQueryCourseMatch = hasPermission({ route: 'courseMatch', function: 'query' });

  //暂时不给兼职老师看
  const canGradeProtectionProgram = hasPermission({
    route: 'grade_protection_program',
    function: 'query',
  });
  const canAppealServicePage = hasPermission({
    route: 'appealService',
    function: 'query',
  });
  const canUpdateAppealServiceResult = hasPermission({
    route: 'appealService',
    function: 'update-result',
  });

  const canAiPromptTemplateQuery = hasPermission({
    route: 'aiPromptTemplate',
    function: 'query',
  });
  const canAiPromptTemplateEdit = hasPermission({
    route: 'aiPromptTemplate',
    function: 'edit',
  });
  const canGradeExtensionImportQuery =
    isSuperAdmin ||
    isAdmin ||
    isOperation ||
    isOperationAdmin ||
    hasPermission({
      route: 'gradeExtensionImport',
      function: 'query',
    });
  const canGradeExtensionImportMatch =
    isSuperAdmin ||
    isAdmin ||
    isOperation ||
    isOperationAdmin ||
    hasPermission({
      route: 'gradeExtensionImport',
      function: 'match',
    });
  const canViewBdManageCity = hasPermission({
    route: 'bd-manage-city',
    function: 'view-bd-manage-city',
  });
  const canViewAcademicOperationMaterialTasks =
    isSuperAdmin || isMatchMaker || isOperation || isOperationAdmin || isOperationSupervisor;
  const canViewAcademicOperationGradingSchemeTasks =
    hasPermission({
      route: 'academicOperationTasks',
      function: 'view',
    });
  const canViewAcademicOperationTasks =
    canViewAcademicOperationGradingSchemeTasks || canViewAcademicOperationMaterialTasks;
  const canUseLlmGateway =
    hasPermission({
      route: 'llmGateway',
      function: 'use',
    }) ||
    permissions?.llmGateway?.includes('use') ||
    currentUser.permissions?.some?.(
      (permission) => permission.route === 'llmGateway' && permission.function === 'use',
    );
  const canQueryResearchType = (type) => !!getResearchTaskScope(hasPermission, type);
  const canViewVideoReviewBoard = canQueryResearchType('videoReview');
  const canViewPartTimeResearchHours = hasPermission({
    route: EDUCATIONAL_RESEARCH_TASKS_ROUTE,
    function: VIEW_PARTTIME_RESEARCH_HOURS_PERMISSION,
  });

  return {
    canQueryOwnSalary: ['all', 'self', 'partial'].some((scope) =>
      hasPermission({ route: 'salary', function: 'query', scope })),
    canQueryLessons: hasPermission({ route: 'lesson', function: 'query' }),
    canQueryWorkplaceSchedule: ['all', 'self'].some((scope) =>
      hasPermission({ route: 'lesson', function: 'query-workplace-schedule', scope })),
    canQueryTeacherCalendar: hasPermission({
      route: 'lesson', function: 'query-teacher-calendar', scope: 'all',
    }),
    canCreateLessonUsage: hasPermission({ route: 'lesson', function: 'create-usage' }),
    canQueryContracts: hasPermission({ route: 'contract', function: 'query' }),
    canViewSigningSummary: hasPermission({
      route: 'data_center',
      function: 'view-signing-summary',
      scope: 'all',
    }),
    canQuerySalesData,
    canQueryKASales,
    canQuerySalaryLessons: ['query', 'verify-paycheck'].some((fn) =>
      hasPermission({ route: 'salary', function: fn, scope: 'all' })),
    canQuerySalarySubsidy,
    canQuerySalaryAdjustment,
    isSuperAdmin,
    isAdmin,
    isHR,
    canViewVideoReviewBoard,
    canViewPartTimeResearchHours,
    isFinance,
    isOperation,
    isOperationAdmin,
    isOperationSupervisor,
    isSales,
    isMatchMaker,
    isRecruiter,
    isEduHR,
    isQualityInspector,
    canSales,
    canCreateRestrictedOrder,
    canAccessSalesLeads,
    canViewSalableProducts,
    canAccessSalesCustomers,
    canCreateSalesOrder,
    canQueryOrders,
    canOrganization,
    canViewAllPerformanceData,
    canAccount,
    canViewTeacherReview,
    canOperation,
    canTeacher,
    canComplaint,
    canQueryCourseMatch,
    canQueryFullTimeTeacherSalary:
      canQueryAllFullTimeTeacherSalary || canQueryPartialFullTimeTeacherSalary,
    canTeam: isSuperAdmin || isAdmin || isHR,
    canManageOperations: hasPermission({
      route: 'operations',
      function: 'view_matchmaker_operations',
    }),
    canAccountProfile: (profileUser) => {
      if (!profileUser || !profileUser?.id) {
        return permissions?.account?.includes?.('create');
      }
      return profileUser.id === currentUser.id || permissions?.account?.includes?.('update');
    },
    canTeacherProfile: (profileUser) => {
      if (!profileUser || !profileUser?.id) {
        return permissions?.teacher?.includes?.('create');
      }
      return profileUser.id === currentUser.id || permissions?.teacher?.includes?.('update');
    },
    canOperationProfile: (profileUser) => {
      if (!profileUser || !profileUser?.id) {
        return permissions?.operations?.includes?.('create');
      }
      return profileUser.id === currentUser.id || permissions?.operations?.includes?.('update');
    },
    canViewPermissionGroups,
    canEditPermissionGroups,
    canEditPermissionGroupMembers,
    canUpdateRolePermissions,
    canUpdateUserPermissions,
    canStudent: hasPermission({ route: 'student', function: 'query' }),
    canPaymentApplication: !!permissions?.paymentApplication?.length,
    canRecruit:
      isSuperAdmin ||
      isAdmin ||
      isHR ||
      isRecruiter ||
      isEduHR ||
      isQualityInspector ||
      canAccessHR,
    canProduct: hasPermission({
      route: 'product',
      function: 'create',
      scope: 'all',
    }),
    canManageProductBonusScheme: permissions?.product?.includes('manage-bonus-scheme'),
    canReviewRenewalBonus: permissions?.bonus?.includes('review-renewal-bonus'),
    canReviewSettlementBonus: permissions?.bonus?.includes('review-settlement-bonus'),
    canReviewActiveStudentBonus: permissions?.bonus?.includes('review-active-student-bonus'),
    canOverwriteBonusAmount: permissions?.bonus?.includes('overwrite-bonus-amount'),
    canDeleteOperationBonus: permissions.bonus?.includes('delete-bonus'),
    canContract,
    canCreateContract: permissions?.contract?.includes('create-savvyuni-contract'),
    canEditContract: permissions?.contract?.includes('edit-contract'),
    canMail: isSuperAdmin || isAdmin || isOperationAdmin || isOperationAdmin,
    hasTrainingCoursePermission: !!permissions?.trainingCourse?.length,
    hasFinancePermission: !!permissions?.finance?.length,
    hasSalaryPermission:
      ['query', 'verify-paycheck'].some((fn) =>
        hasPermission({ route: 'salary', function: fn, scope: 'all' })) ||
      canQuerySalarySubsidy || canQuerySalaryAdjustment,
    canAudit: hasPermission({ route: 'finance', function: 'audit' }),
    hasWithdrawPermission: !!permissions?.withdraw?.length,
    hasZoomPermission: !!permissions?.zoom?.length,
    canSearchLesson: permissions?.lesson?.includes('query'),
    hasAssignmentPermission: !!permissions?.assignment?.length,
    AssignmentProfile:
      permissions?.assignment?.includes('create') || permissions?.assignment?.includes('update'),
    canMarkAssignment: permissions?.assignment?.includes('mark'),
    hasSchoolPermission: !!permissions?.school?.length,
    CoursePage: hasPermission({ route: 'course', function: 'query' }),
    DataCenter: isSuperAdmin || isAdmin,
    Mail: isSuperAdmin || isAdmin || isOperationAdmin,
    canDeleteTrainingCourseRegistration: permissions.trainingCourse?.includes('delete-record'),
    canRegisterTrainingCourse: hasPermission({
      route: 'trainingCourse',
      function: 'create-record',
    }),
    canRegisterSavvyUniCourse: hasPermission({
      route: 'trainingCourse',
      function: 'register-savvyuni-course',
    }),
    canCheckOperationBonus: isSuperAdmin || isAdmin || isFinance,
    //可以查看涨薪板块
    canCheckSalaryIncreaseApprovals: hasPermission({
      route: 'salaryIncreaseApprovals',
      function: 'query',
    }),
    canAccessOnboarding,
    canAccessSecretTeacherFeedback,
    canQueryPayers: permissions.organization?.includes('query-payers'),
    canCreatePayers: permissions.organization?.includes('create-payers'),
    canUpdatePayers: permissions.organization?.includes('update-payers'),
    canEditCode:
      permissions.organization?.includes('edit-code') && permissions.product?.includes('edit-code'),
    canDeclineOrder: permissions.sales?.includes('decline-order'),
    canAssignCourse,
    canAccessSaleData,
    canEditProductRule: permissions.product?.includes('change-product-rule-toc'),
    canAddCourseToSyllabus: permissions?.schoolSemester?.includes('create-course'),
    canCreateSalesChannel: permissions.sales?.includes('create-channel'),
    canUpdateSalesChannel: permissions.sales?.includes('edit-channel'),
    canCreateOrganization: permissions.organization?.includes('create'),
    canUpdateOrganization: permissions.organization?.includes('update'),
    canDeleteOrganization: permissions.organization?.includes('delete'),
    canRestoreOrganization: permissions.organization?.includes('restore'),
    canCreateLesson: permissions.lesson?.includes('create'),
    canMatchStudentNTeacher: permissions.courseMatch?.includes('create'),
    canConfirmSalaryForOthers: permissions.salary?.includes('proxy-confirmation'),
    canQueryNotice: permissions.notice?.includes('get-all-notice'),
    //可以看到教研任务管理
    canQueryResearchTask: canQueryResearchType('management'),
    canViewFirstLesson: canQueryResearchType('firstLesson'),
    canViewPassLessonTaskTable: canViewVideoReviewBoard,
    canViewFirstLessonCommunication: canQueryResearchType('firstLessonCommunication'),
    canViewFirstLessonManagement:
      canQueryResearchType('firstLesson') || canQueryResearchType('firstLessonCommunication'),
    canManageOperationAttendance: permissions.employeeAttendance?.includes('manage'),
    canManageOperationBaseCompensation: permissions.salary?.includes(
      'manage-operation-compensation-plan',
    ),
    canOperationPerformance: hasPermission({
      route: 'salary',
      function: 'view-operation-performance-salary',
    }),
    canOperationBonus: hasPermission({
      route: 'salary',
      function: 'view-operation-bonus-salary',
    }),
    canViewOperationPaystubs: hasPermission({
      route: 'salary',
      function: 'view-operation-paystubs',
    }),
    canSendOperationCourseConsumptionGoodNews: canSendOperationCourseConsumptionRemoteWorkGoodNews,
    canExportOperationPaycheck,
    canCreateCustomBonus: permissions.bonus?.includes('create-custom-bonus'),
    canServiceAgreement,
    canViewTeacherGradeTasks: canQueryResearchType('teacherGrade'),
    canSeeAllSalesPage,
    canFulltimeTeacher,
    canGradeProtectionProgram,
    canAppealServicePage,
    canUpdateAppealServiceResult,
    canAiPromptTemplateQuery,
    canAiPromptTemplateEdit,
    canGradeExtensionImportQuery,
    canGradeExtensionImportMatch,
    canViewMentorOperationsFollow: canViewMentorOperationsFollow,
    canViewMentorOperationsMatchingDashboard,
    canViewRecruitmentPerformance,
    canViewRecruitmentGap,
    canConfigureRecruitmentGap,
    canViewTeacherTraining,
    canManageTeacherTraining,
    canUseLlmGateway,
    canManageStudentTags: isSuperAdmin,
    canServiceData,
    canQueryMentorOperationSalary: isSuperAdmin,
    canViewBdManageCity,
    canViewAcademicOperationTasks,
    canViewAcademicOperationGradingSchemeTasks,
    canViewAcademicOperationMaterialTasks,
    canKnowledgeBank:
      isSuperAdmin ||
      isAdmin ||
      isOperation ||
      isOperationAdmin ||
      isSales ||
      isMatchMaker ||
      isQualityInspector ||
      isSalesAdmin ||
      isOperationSupervisor,
    canManageServiceAccount: hasPermission({
      route: 'serviceAccount',
      function: 'manage',
    }),
    canManagePersonalApiKeys: hasPermission({
      route: 'apiKey',
      function: 'managePersonal',
    }),
  };
}
